import { z } from "zod";
import type { DeliveryStatus } from "./tracking.js";
import { isWorkday } from "./workdays.js";

/**
 * Tracking-Seite (Modul „tracking_page“): eigene Sendungsverfolgung des Kunden für seine Endkunden – im eigenen Design,
 * mit Bausteinen (Composer), zweisprachig (Deutsch/Englisch je Lieferland) und optional unter eigener Domain.
 *
 * Endkunden sehen Status, Verlauf, Paketinhalt und den Zielort (PLZ, Ort) – nie Name oder Straße, damit ein
 * weitergeleiteter Link nichts preisgibt.
 */

export const TRACKING_PAGE_LANGS = ["de", "en"] as const;
export type TrackingPageLang = (typeof TRACKING_PAGE_LANGS)[number];

/** Text je Sprache – fehlt Englisch, gilt Deutsch */
const ltext = (max: number) => z.object({ de: z.string().trim().max(max).default(""), en: z.string().trim().max(max).default("") }).default({ de: "", en: "" });
export type LText = { de: string; en: string };
export const ltextOf = (t: LText | undefined | null, lang: TrackingPageLang): string => (t ? (lang === "en" ? t.en || t.de : t.de || t.en) : "");

/** Links nur https/http, mailto, tel – nie javascript: o. Ä. */
export const safeLink = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === "" || /^(https?:\/\/[^\s<>"]+|mailto:[^\s<>"]+|tel:[+\d\s()/-]+)$/i.test(v), "Link muss mit https://, mailto: oder tel: beginnen.");

const assetId = z.string().uuid().nullable().default(null);
const color = z.string().regex(/^#[0-9a-f]{6}$/i, "Farbe als #RRGGBB");

/** Phasen aus Sicht des Endkunden (vereinfachter Ablauf, gleich für alle Dienstleister) */
export const CUSTOMER_PHASES = ["ordered", "processing", "shipped", "in_transit", "out_for_delivery", "delivered"] as const;
export type CustomerPhase = (typeof CUSTOMER_PHASES)[number] | "pickup_ready" | "problem" | "returned" | "cancelled";
/** Bausteine können nur in bestimmten Phasen erscheinen („nur bei Zustellproblem“) */
export const SHOW_WHEN = ["before_shipping", "in_transit", "out_for_delivery", "delivered", "problem"] as const;
export type ShowWhen = (typeof SHOW_WHEN)[number];

const base = { id: z.string().min(1).max(40), showWhen: z.array(z.enum(SHOW_WHEN)).max(SHOW_WHEN.length).default([]) };

export const trackingBlockSchema = z.discriminatedUnion("type", [
  z.object({ ...base, type: z.literal("hero"), image: assetId, title: ltext(120), text: ltext(400), buttonLabel: ltext(40), buttonUrl: safeLink.default(""), height: z.enum(["compact", "tall"]).default("compact") }),
  z.object({ ...base, type: z.literal("status"), style: z.enum(["steps", "minimal"]).default("steps"), showEvents: z.boolean().default(true), headline: ltext(120) }),
  z.object({ ...base, type: z.literal("eta"), headline: ltext(80) }),
  z.object({ ...base, type: z.literal("items"), headline: ltext(80), showImages: z.boolean().default(true) }),
  z.object({ ...base, type: z.literal("promo"), image: assetId, title: ltext(120), text: ltext(300), code: z.string().trim().max(40).default(""), buttonLabel: ltext(40), buttonUrl: safeLink.default(""), background: color.nullable().default(null) }),
  z.object({
    ...base,
    type: z.literal("products"),
    headline: ltext(80),
    items: z.array(z.object({ id: z.string().min(1).max(40), image: assetId, title: ltext(80), price: z.string().trim().max(30).default(""), url: safeLink.default("") })).max(8).default([]),
  }),
  z.object({ ...base, type: z.literal("text"), headline: ltext(120), body: ltext(2000), align: z.enum(["left", "center"]).default("left") }),
  z.object({ ...base, type: z.literal("faq"), headline: ltext(80), items: z.array(z.object({ id: z.string().min(1).max(40), q: ltext(200), a: ltext(1500) })).max(15).default([]) }),
  z.object({
    ...base,
    type: z.literal("buttons"),
    items: z.array(z.object({ id: z.string().min(1).max(40), label: ltext(40), url: safeLink.default(""), style: z.enum(["primary", "secondary"]).default("secondary") })).max(4).default([]),
  }),
  z.object({
    ...base,
    type: z.literal("social"),
    headline: ltext(80),
    links: z
      .object({ instagram: safeLink, tiktok: safeLink, facebook: safeLink, youtube: safeLink, pinterest: safeLink, x: safeLink, linkedin: safeLink })
      .partial()
      .default({}),
  }),
  z.object({ ...base, type: z.literal("newsletter"), title: ltext(120), text: ltext(300), buttonLabel: ltext(40), buttonUrl: safeLink.default("") }),
  z.object({ ...base, type: z.literal("image"), image: assetId, url: safeLink.default(""), alt: ltext(120) }),
]);
export type TrackingBlock = z.infer<typeof trackingBlockSchema>;
export type TrackingBlockType = TrackingBlock["type"];

export const TRACKING_FONTS = ["modern", "geometric", "elegant", "rounded"] as const;
export const trackingThemeSchema = z.object({
  logo: assetId,
  /** Klick auf das Logo */
  shopUrl: safeLink.default(""),
  brand: color.default("#111827"),
  accent: color.default("#f97361"),
  background: z.enum(["light", "soft", "dark", "brand"]).default("soft"),
  font: z.enum(TRACKING_FONTS).default("modern"),
  radius: z.enum(["sharp", "soft", "round"]).default("round"),
  /** kleine Fußzeile, z. B. Impressum-Link */
  footer: ltext(300),
  imprintUrl: safeLink.default(""),
  privacyUrl: safeLink.default(""),
});
export type TrackingTheme = z.infer<typeof trackingThemeSchema>;

export const trackingPageConfigSchema = z.object({
  version: z.literal(1).default(1),
  /** Seitentitel im Browser */
  title: ltext(80),
  theme: trackingThemeSchema.default(trackingThemeSchema.parse({})),
  blocks: z.array(trackingBlockSchema).max(30).default([]),
});
export type TrackingPageConfig = z.infer<typeof trackingPageConfigSchema>;

/** Status muss genau einmal vorkommen – sonst sieht der Endkunde nicht, wo sein Paket ist */
export function trackingConfigError(c: TrackingPageConfig): string | null {
  const status = c.blocks.filter((b) => b.type === "status").length;
  if (status !== 1) return "Der Baustein „Sendungsstatus“ muss genau einmal auf der Seite sein.";
  const ids = new Set<string>();
  for (const b of c.blocks) {
    if (ids.has(b.id)) return "Doppelte Baustein-ID.";
    ids.add(b.id);
  }
  return null;
}

let seq = 0;
export const newBlockId = () => `b${Date.now().toString(36)}${(seq++).toString(36)}`;
const lt = (de: string, en: string): LText => ({ de, en });

/** Startseite für neue Kunden: sofort ansehnlich, alles änderbar */
export function defaultTrackingPageConfig(shop: string): TrackingPageConfig {
  return trackingPageConfigSchema.parse({
    title: lt(`Sendungsverfolgung – ${shop}`, `Order tracking – ${shop}`),
    theme: { footer: lt(`© ${shop}`, `© ${shop}`) },
    blocks: [
      { id: "hero", type: "hero", title: lt("Danke für deine Bestellung!", "Thanks for your order!"), text: lt("Hier siehst du jederzeit, wo dein Paket gerade ist.", "Follow your parcel every step of the way.") },
      { id: "status", type: "status" },
      { id: "eta", type: "eta" },
      { id: "items", type: "items" },
      { id: "help", type: "buttons", showWhen: ["problem"], items: [{ id: "contact", label: lt("Kontakt aufnehmen", "Contact us"), url: "", style: "primary" }] },
      { id: "faq", type: "faq", headline: lt("Häufige Fragen", "FAQ"), items: [
        { id: "f1", q: lt("Wann kommt mein Paket?", "When will my parcel arrive?"), a: lt("Die voraussichtliche Zustellung siehst du oben. Sie wird laufend mit den Daten des Versanddienstleisters aktualisiert.", "You can see the estimated delivery above. It is updated continuously with the carrier's data.") },
        { id: "f2", q: lt("Ich war nicht zu Hause – was nun?", "I wasn't at home – what now?"), a: lt("Der Zusteller hinterlässt eine Benachrichtigung. Meist liegt das Paket am nächsten Werktag in einer Filiale oder Packstation bereit.", "The courier leaves a notice. Usually the parcel is ready for pickup at a nearby post office or locker the next working day.") },
      ] },
    ],
  });
}

/** Neuer Baustein mit sinnvollen Vorgaben */
export function newTrackingBlock(type: TrackingBlockType): TrackingBlock {
  const id = newBlockId();
  const presets: Record<TrackingBlockType, unknown> = {
    hero: { id, type, title: lt("Danke für deine Bestellung!", "Thanks for your order!") },
    status: { id, type },
    eta: { id, type },
    items: { id, type },
    promo: { id, type, title: lt("10 % auf deine nächste Bestellung", "10% off your next order"), text: lt("Mit diesem Code sparst du beim nächsten Einkauf.", "Use this code on your next purchase."), code: "DANKE10", buttonLabel: lt("Zum Shop", "Shop now") },
    products: { id, type, headline: lt("Das könnte dir auch gefallen", "You might also like") },
    text: { id, type, headline: lt("Überschrift", "Headline"), body: lt("Dein Text", "Your text") },
    faq: { id, type, headline: lt("Häufige Fragen", "FAQ"), items: [{ id: `${id}q`, q: lt("Frage", "Question"), a: lt("Antwort", "Answer") }] },
    buttons: { id, type, items: [{ id: `${id}a`, label: lt("Retoure anmelden", "Start a return"), url: "", style: "secondary" }] },
    social: { id, type, headline: lt("Folge uns", "Follow us") },
    newsletter: { id, type, title: lt("Nichts mehr verpassen", "Stay in the loop"), text: lt("Neuheiten und Angebote direkt in dein Postfach.", "New arrivals and offers straight to your inbox."), buttonLabel: lt("Jetzt anmelden", "Sign up") },
    image: { id, type },
  };
  return trackingBlockSchema.parse(presets[type]);
}

// ── Ablauf aus Sicht des Endkunden ──

/** Phase aus Auftragsstatus und Zustellstatus (Gesamtstand aller Pakete) */
export function customerPhase(orderStatus: string, delivery: DeliveryStatus | null | undefined, shipped: boolean): CustomerPhase {
  if (orderStatus === "cancelled") return "cancelled";
  if (delivery === "returned") return "returned";
  if (delivery === "exception") return "problem";
  if (delivery === "delivered") return "delivered";
  if (delivery === "pickup_ready") return "pickup_ready";
  if (delivery === "out_for_delivery") return "out_for_delivery";
  if (delivery === "in_transit") return "in_transit";
  if (shipped || delivery === "pre_transit" || orderStatus === "shipped" || orderStatus === "partially_shipped") return "shipped";
  if (orderStatus === "submitted" || orderStatus === "in_progress") return "processing";
  return "ordered";
}

/** Fortschritt 0–5 auf der Schrittleiste */
export function phaseStep(p: CustomerPhase): number {
  switch (p) {
    case "ordered": return 0;
    case "processing": return 1;
    case "shipped": return 2;
    case "in_transit": case "problem": case "returned": return 3;
    case "cancelled": return 0;
    case "out_for_delivery": case "pickup_ready": return 4;
    case "delivered": return 5;
  }
}

/** Gehört die Phase zu einer Sichtbarkeits-Bedingung eines Bausteins? */
export function blockVisible(showWhen: ShowWhen[], p: CustomerPhase): boolean {
  if (!showWhen.length) return true;
  const group: ShowWhen = p === "ordered" || p === "processing" ? "before_shipping" : p === "shipped" || p === "in_transit" ? "in_transit" : p === "out_for_delivery" || p === "pickup_ready" ? "out_for_delivery" : p === "delivered" ? "delivered" : p === "cancelled" ? "before_shipping" : "problem";
  return showWhen.includes(group);
}

const ymd = (d: Date) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(d);
function addWorkdays(from: Date, n: number): string {
  let day = ymd(from);
  for (let i = 0; i < n; ) {
    const d = new Date(`${day}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    day = d.toISOString().slice(0, 10);
    if (isWorkday(day)) i++;
  }
  return day;
}

/** typische Laufzeit in Werktagen nach Zielland (Deutschland aus dem deutschen Lager) */
export function transitDays(country: string | null | undefined): [number, number] {
  const c = (country ?? "DE").toUpperCase();
  if (c === "DE") return [1, 2];
  if (["AT", "NL", "BE", "LU", "DK", "FR", "CZ", "PL", "CH", "LI"].includes(c)) return [2, 4];
  if (["IT", "ES", "PT", "SE", "FI", "IE", "HU", "SK", "SI", "HR", "RO", "BG", "GR", "EE", "LV", "LT", "NO", "GB"].includes(c)) return [3, 6];
  return [5, 10];
}

/**
 * Voraussichtliche Zustellung (YYYY-MM-DD, von–bis) – grobe Orientierung aus Versandtag und Zielland,
 * „heute“ sobald in Zustellung. Vor dem Versand: ab heute + Bearbeitung (1 Werktag).
 */
export function estimateDelivery(p: CustomerPhase, shippedAt: Date | null, country: string | null | undefined, now = new Date()): { from: string; to: string } | null {
  if (p === "delivered" || p === "returned" || p === "problem" || p === "cancelled") return null;
  if (p === "out_for_delivery" || p === "pickup_ready") return { from: ymd(now), to: ymd(now) };
  const [min, max] = transitDays(country);
  const start = shippedAt ?? new Date(`${addWorkdays(now, 1)}T10:00:00Z`);
  let from = addWorkdays(start, min);
  const to = addWorkdays(start, max);
  // nicht in der Vergangenheit schätzen (verspätete Pakete)
  const today = ymd(now);
  if (to < today) return { from: today, to: addWorkdays(now, 1) };
  if (from < today) from = today;
  return { from, to };
}

// ── Adresse der Seite ──

/** Kurzname in der Adresse (app.logiful.de/t/<slug>) */
export function trackingSlug(name: string): string {
  const s = name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
  return s.length >= 3 ? s : `shop-${s || "x"}`;
}
export const slugSchema = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/, "Nur Kleinbuchstaben, Ziffern und Bindestriche (3–40 Zeichen).");

/** Eigene Domain: nur Subdomains (CNAME geht nicht auf der Hauptdomain), kein Logiful-/Render-Name */
export function normalizeTrackingDomain(input: string): string {
  return input.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}
export function trackingDomainError(domain: string): string | null {
  if (!/^(?=.{4,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(domain)) return "Bitte eine gültige Domain angeben, z. B. track.dein-shop.de.";
  if (domain.split(".").length < 3) return "Bitte eine Subdomain verwenden (z. B. track.dein-shop.de) – auf der Hauptdomain ist kein CNAME-Eintrag möglich.";
  if (/(^|\.)(logiful\.de|onrender\.com|localhost)$/.test(domain)) return "Diese Domain kann nicht verwendet werden.";
  return null;
}

// ── Daten für die öffentliche Seite ──

export interface TrackingEventView {
  at: string;
  text: string;
  location: string | null;
}
export interface TrackingPackageView {
  trackingNumber: string;
  carrier: string;
  status: DeliveryStatus | null;
  /** Link zum Dienstleister (Fallback) */
  carrierUrl: string | null;
  deliveredAt: string | null;
  events: TrackingEventView[];
}
export interface TrackingOrderView {
  orderNumber: string;
  placedAt: string;
  phase: CustomerPhase;
  step: number;
  shippedAt: string | null;
  deliveredAt: string | null;
  eta: { from: string; to: string } | null;
  /** nur Ort – nie Name/Straße */
  destination: { zip: string; city: string; country: string };
  items: { name: string; quantity: number; image: string | null }[];
  packages: TrackingPackageView[];
  /** Sprache nach Lieferland */
  lang: TrackingPageLang;
}
export interface TrackingSite {
  slug: string;
  shop: string;
  config: TrackingPageConfig;
  search: boolean;
  /** Basis der Bild-URLs */
  assetBase: string;
}
